import Button from '@app/shared/components/button/Button';
import Plus from '@app/shared/icons/Plus';
import { ButtonAppearance } from '@app/shared/components/button/button-enums';
import { useLocale } from '@app/core/hooks/useLocale';
import { useAppDispatch, useAppSelector } from '@app/core/hooks/redux-hooks';
import { SelectOption } from '@app/shared/models/option';
import { dateFormatter } from '@app/shared/formatters';
import { projectSlice } from '@app/core/store/project-slice';
import { cloneToMutableData } from '@app/shared/helpers';
import { ExitScenarioInputDto } from '@app/shared/models/contracts/project-dto';
import { MAX_WATERFALLS } from './constants';
import {
  DEFAULT_SCENARIO_KEY,
  getNextScenarioKey,
  getScenarioColumnKeys,
} from './scenario-columns';

export const AddSampleWaterfall = () => {
  const { l } = useLocale();
  const dispatch = useAppDispatch();

  const project = useAppSelector((state) => state.project.projectDraft);
  const waterfallInputs = project.opmInput.exitScenarios ?? {};
  const opmCalculation = useAppSelector((state) => state.opmCalculation);
  const opmWaterfall = opmCalculation?.riskFreeValues.waterfalls;

  let yearOptions: SelectOption[] = [];

  if (opmWaterfall && Object.keys(opmWaterfall).length > 0) {
    yearOptions = Object.keys(opmWaterfall).map((key) => ({
      value: key,
      viewValue: dateFormatter(key, { month: 'short', year: 'numeric' }),
    }));
  }

  const handleAddSampleWaterfall = () => {
    const columnKeys = getScenarioColumnKeys(waterfallInputs);
    const lastScenario = waterfallInputs[columnKeys[columnKeys.length - 1]];

    // the new scenario mirrors the last column so that it is populated straight away,
    // in line with how scenarios are added elsewhere in the application
    const newSampleWaterfall: ExitScenarioInputDto = lastScenario
      ? { ...lastScenario }
      : {
          exitDate: yearOptions[0].value!.toString(),
          enterpriseValue: null,
        };

    // when nothing has been stored yet the table still renders an implicit first column,
    // so that column has to be persisted alongside the newly added one
    const existingWaterfall =
      Object.keys(waterfallInputs).length > 0
        ? cloneToMutableData(waterfallInputs)
        : { [DEFAULT_SCENARIO_KEY]: { ...newSampleWaterfall } };

    const updatedWaterfall = {
      ...existingWaterfall,
      [getNextScenarioKey(waterfallInputs)]: newSampleWaterfall,
    };

    dispatch(
      projectSlice.actions.updateProjectDraftInternal({
        ...project,
        opmInput: {
          ...project.opmInput,
          exitScenarios: updatedWaterfall,
        },
      })
    );
  };

  const isDisabled =
    yearOptions.length === 0 || getScenarioColumnKeys(waterfallInputs).length >= MAX_WATERFALLS;

  return (
    <Button
      appearance={ButtonAppearance.DEFAULT_PRIMARY}
      startIcon={<Plus />}
      onClick={() => handleAddSampleWaterfall()}
      disabled={isDisabled}>
      {l('_AddScenario')}
    </Button>
  );
};
